import React, { useState } from 'react';
import { Text } from 'react-native';
import { FaLink, FaShareAlt, FaTimes, FaCheck } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../theme/ThemeContext';
import { API_BASE_URL } from '../config';

const TeamsIcon = () => (
  <img
    src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQychPQjxHP_T53JXZDtA3L5BHAu_REHa92NQ&s"
    alt="Microsoft Teams"
    style={{ width: 20, height: 20, objectFit: 'contain', borderRadius: 4 }}
  />
);

/**
 * Unified share sheet for tweets, narratives, and action queue items.
 *
 * Tweet mode:   <ShareModal tweet={tweet} onClose={fn} />
 * Content mode: <ShareModal type="narrative"|"action" id={id} title={title} summary={summary} onClose={fn} />
 */
const ShareModal = ({ tweet, type, id, title, summary, onClose }) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [copied, setCopied] = useState(false);

  const isTweetMode = !!tweet;

  const shareUrl = isTweetMode
    ? (tweet.url || '')
    : type === 'narrative'
      ? `${window.location.origin}/trends?narrative=${id}`
      : `${API_BASE_URL}/share/${type}/${id}`;

  const bodyText = isTweetMode ? (tweet.tweet_content ?? '') : (summary ?? '');
  const displayName = isTweetMode ? tweet.author_name : title;

  const message = isTweetMode
    ? `${displayName}: "${bodyText.slice(0, 200)}${bodyText.length > 200 ? '…' : ''}"\n\nShared via ReactFirst AI`
    : `${displayName}\n\n${bodyText.slice(0, 300)}${bodyText.length > 300 ? '…' : ''}\n\nShared via ReactFirst AI`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* silent */ }
  };

  const handleNativeShare = () => {
    navigator.share({ url: shareUrl, title: displayName || '', text: bodyText.slice(0, 200) });
  };

  const handleShareToTeams = async () => {
    try {
      const { app, sharing } = await import('@microsoft/teams-js');
      await app.initialize();
      sharing.shareWebContent({
        content: [{ type: 'URL', url: shareUrl, message, preview: true }]
      });
    } catch {
      window.open(
        `https://teams.microsoft.com/share?href=${encodeURIComponent(shareUrl)}&preview=true&msgText=${encodeURIComponent(message)}`,
        '_blank'
      );
    }
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: theme.overlay,
      backdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 3000,
      padding: 20,
    }} onClick={onClose}>
      <div style={{
        backgroundColor: theme.bgCard,
        border: `1px solid ${theme.border}`,
        borderRadius: 20,
        width: '100%',
        maxWidth: 400,
        boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        overflow: 'hidden',
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '20px 20px 16px',
          borderBottom: `1px solid ${theme.border}`,
        }}>
          <Text style={{ color: theme.textPrimary, fontSize: 17, fontWeight: '700', fontFamily: 'Outfit, sans-serif' }}>
            {t('app.shareModal.title')}
          </Text>
          <div
            onClick={onClose}
            style={{
              width: 30, height: 30, borderRadius: 8,
              backgroundColor: theme.bgSurface,
              border: `1px solid ${theme.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <FaTimes style={{ color: theme.textSecondary, fontSize: 13 }} />
          </div>
        </div>

        {/* Options */}
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>

          {/* Copy Link */}
          <div
            onClick={handleCopyLink}
            style={{
              ...rowStyle(theme),
              ...(copied ? { borderColor: '#30d158', backgroundColor: 'rgba(48,209,88,0.08)' } : {}),
            }}
          >
            <div style={iconBox(theme, copied ? '#30d158' : null)}>
              {copied
                ? <FaCheck style={{ fontSize: 15, color: '#30d158' }} />
                : <FaLink style={{ fontSize: 15, color: theme.textSecondary }} />
              }
            </div>
            <Text style={{ color: copied ? '#30d158' : theme.textPrimary, fontSize: 15, fontWeight: '500', fontFamily: 'Outfit, sans-serif' }}>
              {copied ? t('app.shareModal.copied') : t('app.shareModal.copyLink')}
            </Text>
          </div>

          {/* Share via… (native share API) */}
          {typeof navigator !== 'undefined' && !!navigator.share && (
            <div onClick={handleNativeShare} style={rowStyle(theme)}>
              <div style={iconBox(theme, null)}>
                <FaShareAlt style={{ fontSize: 15, color: theme.textSecondary }} />
              </div>
              <Text style={{ color: theme.textPrimary, fontSize: 15, fontWeight: '500', fontFamily: 'Outfit, sans-serif' }}>
                {t('app.shareModal.shareVia')}
              </Text>
            </div>
          )}

          {/* Share to Teams */}
          <div
            onClick={handleShareToTeams}
            style={{
              ...rowStyle(theme),
              borderColor: 'rgba(80,89,201,0.3)',
              backgroundColor: 'rgba(80,89,201,0.07)',
            }}
          >
            <div style={{ ...iconBox(theme, null), backgroundColor: 'rgba(80,89,201,0.15)', border: '1px solid rgba(80,89,201,0.3)' }}>
              <TeamsIcon />
            </div>
            <Text style={{ color: theme.textPrimary, fontSize: 15, fontWeight: '500', fontFamily: 'Outfit, sans-serif' }}>
              {t('app.shareModal.shareToTeams')}
            </Text>
            <Text style={{ color: theme.textMuted, fontSize: 12, marginLeft: 'auto', fontFamily: 'Outfit, sans-serif' }}>
              {t('app.shareModal.microsoftTeams')}
            </Text>
          </div>

        </div>
      </div>
    </div>
  );
};

const rowStyle = (theme) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 12,
  padding: '12px 16px',
  borderRadius: 14,
  border: `1px solid ${theme.border}`,
  backgroundColor: theme.bgSurface,
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  userSelect: 'none',
});

const iconBox = (theme, accentColor) => ({
  width: 36,
  height: 36,
  borderRadius: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: accentColor ? `${accentColor}18` : theme.bgElevated,
  border: `1px solid ${accentColor ? `${accentColor}40` : theme.border}`,
  flexShrink: 0,
});

export default ShareModal;
